import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    Input,
    LOCALE_ID,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import { BaseComponent } from 'src/app/_core/tools/base.component';
import { ReferenceYearsState } from 'src/app/_shared/stores/reference-years/reference-years.state';
import {
    AttributionBudgetModelDto,
    ChampDto,
    ModeleRegleSubQuotaModeleDto,
    MoteurRegleTypeDateRefDto,
    RegleSubQuotaListeValModelDto
} from 'src/graphql/generated-types';
import { SharedSubventionCriteriaState } from 'src/app/_shared/stores/shared-subvention-criteria/shared-subvention-criteria-state';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { UntypedFormControl } from '@angular/forms';
import { KalTableHeader } from 'src/app/_shared/modules/kal-table/classes/kal-table-header';
import { constants } from 'src/environments/constants';
import { formatCurrency, formatNumber } from '@angular/common';
import { CURRENCY } from 'src/app/_core/core.module';

@Component({
    selector: 'app-shared-allocation-detail-envelope',
    templateUrl: './shared-allocation-detail-envelope.component.html',
    styleUrls: ['./shared-allocation-detail-envelope.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SharedAllocationDetailEnvelopeComponent extends BaseComponent implements OnInit {

    @Input()
    type: string;

    formControl = new UntypedFormControl(false);

    sharedColumns: KalTableHeader<RegleSubQuotaListeValModelDto & { Valeur }>[] = [
        {code: 'Od', label: 'Ouvrant Droit'},
        {code: 'Conjoint', label: 'Conjoint'},
        {code: 'Enfant', label: 'Enfant'},
        {code: 'Externe', label: 'Externe'},
        {code: 'ValMin', label: 'Valeur min'},
        {code: 'ValMax', label: 'Valeur max'}
    ];

    constructor(protected readonly cdr: ChangeDetectorRef,
                @Inject(LOCALE_ID) private readonly locale: string,
                @Inject(CURRENCY) private readonly currency: string
    ) {
        super(cdr);
    }

    private _allocation$ = new BehaviorSubject<AttributionBudgetModelDto>(null);

    get allocation$(): Observable<AttributionBudgetModelDto> {
        return this._allocation$.asObservable();
    }

    get allocation(): AttributionBudgetModelDto {
        return this._allocation$.getValue();
    }

    @Input()
    set allocation(allocation: AttributionBudgetModelDto) {
        this._allocation$.next(allocation);
    }

    get childrenSelectedDate(): string {
        if (['SAI', 'SAIINF'].some(type => this.referenceDate?.Code === type)) {
            return ` (${this.howMuch.DateRefPondJour} / ${this.howMuch.DateRefPondMois})`;
        }

        return '';
    }

    get howMuch(): ModeleRegleSubQuotaModeleDto {
        return this.allocation?.MoteurRegleSubQta?.ModeleRegleSubQta;
    }

    get referenceDate(): MoteurRegleTypeDateRefDto {
        return this.snapshot(ReferenceYearsState.referenceMotorDatesList).find(
            ({Id}) => Id === this.howMuch.IdTypeDateRefPond
        );
    }

    get isFixed(): boolean {
        return !this.howMuch?.IdChamp;
    }

    get fixedValues(): RegleSubQuotaListeValModelDto {
        return this.rulesList[0];
    }

    get rulesList(): RegleSubQuotaListeValModelDto[] {
        return this.howMuch?.ListeModeleRegleSubQuotaValModele?.RegleSubQuotaListeValModelDTO || [];
    }

    get defaultValues(): RegleSubQuotaListeValModelDto {
        return this.rulesList.find(({ChpLstValId, De, A}) => !ChpLstValId && !De && !A);
    }

    get displayedRulesList(): RegleSubQuotaListeValModelDto[] {
        const defaultValues = this.defaultValues;
        const criterion = this.criterion;
        let rules;
        if (criterion?.ChampTypeDonnee?.Code === constants.fieldsTypesCodes.LST) {
            rules = criterion.ListChampListeValeur.ChampListeValeurDTO.filter(({IsCloture}) =>
                !IsCloture
            ).map(({Valeur, Id}) => ({
                    ...this.rulesList.find(({ChpLstValId}) => +ChpLstValId === Id),
                    Valeur
                })
            );
        } else {
            rules = this.rulesList.filter(rule => rule !== defaultValues);
        }
        return rules;
    }

    get criterion(): ChampDto {
        if (!this.howMuch?.IdChamp) {
            return null;
        }

        return this.snapshot(SharedSubventionCriteriaState.criteriaList).find(({Id}) =>
            Id === this.howMuch.IdChamp
        );
    }

    get criterionLabel(): string {
        return this.criterion?.Libelle;
    }

    get columns(): KalTableHeader<RegleSubQuotaListeValModelDto & { Valeur }>[] {
        if (this.criterion?.ChampTypeDonnee?.Code === 'LST') {
            return [
                {code: 'Valeur', label: 'Valeur'},
                ...this.sharedColumns
            ];
        }
        return [
            {code: 'De', label: 'De'},
            {code: 'A', label: 'A'},
            ...this.sharedColumns
        ];
    }

    get hasChildrenParameters(): boolean {
        if (!this.howMuch) {
            return false;
        }

        const childrenParametersKeys = [
            'RglModeleIdAgeEnfMax',
            'RglModeleIdAgeEnfMin',
            'RglModeleIdEnfAch',
            'RglModeleIdEnfDir',
            'RglModeleIdEnfHan',
        ];
        return childrenParametersKeys.some(key =>
            !!this.howMuch[key]
        );
    }

    get hasWeightParameter(): boolean {
        if (!this.howMuch) {
            return false;
        }

        const childrenParametersKeys = [
            'IsPondererMoisEntier',
            'IsPondererMoisPresence',
            'IsPondererTempsTravail',
        ];
        return childrenParametersKeys.some(key =>
            !!this.howMuch[key]
        );
    }

    get isArticle(): boolean {
        return this.type === 'ART';
    }

    getDisplayedValue(value: number): string {
        if (this.isArticle) {
            return formatNumber(value, this.locale, '1.0-0');
        }
        return formatCurrency(value, this.locale, this.currency);
    }

    ngOnInit(): void {
        this.watch$ = this.allocation$.pipe(
            tap(() => this.formControl.patchValue(this.isFixed))
        );
    }

}
